/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import axios from 'axios';
import * as api from '@codegrade/api';
import * as utils from '@codegrade/utils';
import { isApiError } from './api';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class AggregateError extends Error {
    errors;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(errors) {
        super('Multiple errors occurred');
        this.errors = errors;
    }
}
/**
 * Get the error message from an error(-like) objects.
 *
 * @param err The error to get the message from.
 * @returns The error message, or a generic message if we could not find one.
 */
export function getErrorMessage(err) {
    let msg;
    if (err == null) {
        // TODO: Find out why we have this case, and eliminate it because it
        // does not really make sense to throw a `null`.
        return '';
    }
    if (err instanceof api.BaseError && err.message != null) {
        return err.message;
    }
    else if (err instanceof api.ApiResultError && err.error?.message != null) {
        return err.error.message;
    }
    else if (err != null && axios.isAxiosError(err)) {
        // TODO: Should we rewrite this case in terms of instanceof?
        let errData = err.response?.data;
        if (errData instanceof ArrayBuffer) {
            const message = new TextDecoder().decode(errData);
            try {
                errData = JSON.parse(message);
            }
            catch (e) {
                errData = message;
            }
        }
        const msgAttr = 'message';
        if (errData != null && Object.prototype.hasOwnProperty.call(errData, msgAttr)) {
            msg = errData[msgAttr];
        }
        else if (errData != null) {
            msg = `Something unknown went wrong: "${errData.toString()}"`;
        }
        else {
            msg = null;
        }
    }
    else if (err instanceof Error) {
        msg = err.message;
    }
    else if ('message' in err && typeof err.message === 'string') {
        msg = err.message;
    }
    else {
        // TODO: Find out why we have this case, and eliminate its use by just
        // throwing errors.
        msg = err.toString();
    }
    return msg ?? 'Something unknown went wrong';
}
// We do not need consistent return, because we always throw if the status code
// is invalid.
/* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
function statusPattern(status) {
    if (status >= 100) {
        if (status < 200) {
            return '1xx';
        }
        else if (status < 300) {
            return '2xx';
        }
        else if (status < 400) {
            return '3xx';
        }
        else if (status < 500) {
            return '4xx';
        }
        else if (status < 600) {
            return '5xx';
        }
    }
    return utils.assert.AssertionError.assert(false, 'HTTP status code must be in the range [100, 600)');
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function handleHttpError(handlers, err) {
    const status = err.response?.status;
    if (status != null) {
        const exactHandler = handlers[status];
        if (exactHandler != null) {
            return exactHandler(err);
        }
        const patternHandler = handlers[statusPattern(status)];
        if (patternHandler != null) {
            return patternHandler(err);
        }
        const defaultHandler = handlers?.default;
        if (defaultHandler != null) {
            return defaultHandler(err);
        }
    }
    else {
        const noResHandler = handlers?.noResponse;
        if (noResHandler != null) {
            return noResHandler(err);
        }
    }
    throw err;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function makeHttpErrorHandler(handlers) {
    return err => handleHttpError(handlers, err);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function retryPromise(promise, shouldRetry, retryBackoff) {
    return promise().catch(err => {
        if (shouldRetry(err)) {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    retryPromise(promise, shouldRetry, retryBackoff).then(resolve, reject);
                }, retryBackoff());
            });
        }
        else {
            throw err;
        }
    });
}
/**
 * The actual logic of the `tryHandleApiError` function.
 *
 * @param handlers See documentation on `ApiErrorHandlers` type.
 * @param err The error we are handling.
 * @returns The type results from the handlers.
 */
function innerHandleApiError(handlers, err) {
    if (isApiError(err)) {
        const status = err.response.status;
        const exactHandler = handlers[status];
        if (exactHandler != null) {
            return exactHandler(err);
        }
        const patternHandler = handlers[statusPattern(status)];
        if (patternHandler != null) {
            return patternHandler(err);
        }
        const defaultHandler = handlers.default;
        if (defaultHandler != null) {
            return defaultHandler(err);
        }
    }
    if (!(err instanceof api.ApiResultError) && handlers.noResponse != null) {
        return handlers.noResponse(utils.errors.coerceToError(err));
    }
    return handlers._(utils.errors.coerceToError(err));
}
/**
 * A currying function that try handle a provided error using a map with `ApiErrorHandlers` type.
 *
 * @param handlers See documentation on `ApiErrorHandlers` type.
 * @param err The error to be handled.
 * @returns The result, or the function itself with arguments curried,
 *          depending on the amount of incoming arguments.
 */
export function tryHandleApiError(handlers, err) {
    switch (arguments.length) {
        case 0:
            return tryHandleApiError;
        case 1:
            return (correctErr) => innerHandleApiError(handlers, correctErr);
        default:
            return innerHandleApiError(handlers, err);
    }
}
